import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import { FOUNDERS, FACULTY } from "@/lib/data";

export default function Instructors() {
  const ceo = FOUNDERS.find((f) => f.id === "manikandan-g");
  const coFounders = FOUNDERS.filter((f) => f.id !== "manikandan-g");

  return (
    <div data-testid="instructors-page">
      {/* Hero */}
      <section className="relative h-[44vh] min-h-[360px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1547153760-18fc86324498?w=2000"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Founders & Our Team</p>
          <h1 className="font-display text-5xl lg:text-7xl text-white leading-[0.95]">
            The minds behind <span className="phoenix-text">Phoenix.</span>
          </h1>
        </div>
      </section>

      {/* Founder & CEO — top center */}
      <section className="py-20 lg:py-28 max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="Founder & CEO"
          title="The visionary"
          accent="behind it all."
          align="center"
          testid="ceo-heading"
        />
        <div className="max-w-md mx-auto">
          <div
            data-testid={`founder-${ceo.id}`}
            className="bg-[#0a0a0a] border-2 border-[#ff4500]/40 card-hover overflow-hidden"
          >
            <div className="relative aspect-[4/5] image-zoom overflow-hidden bg-[#0a0a0a]">
              <img
                src={ceo.image}
                alt={ceo.name}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
              <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-black/70 backdrop-blur border border-[#ffb700]/40 text-[#ffb700]">
                {ceo.flag} {ceo.city} · {ceo.branch}
              </span>
            </div>
            <div className="p-7 lg:p-8 text-center">
              <p className="text-xs uppercase tracking-[0.25em] text-[#ffb700]">{ceo.title}</p>
              <h3 className="font-display text-3xl lg:text-4xl text-white mt-3">{ceo.name}</h3>
              <p className="text-sm text-white/65 mt-4 leading-relaxed">{ceo.short}</p>
              <Link to={`/instructors/${ceo.id}`}>
                <button
                  data-testid={`view-profile-${ceo.id}`}
                  className="mt-6 btn-phoenix bg-[#ff4500] text-white px-6 h-12 uppercase tracking-wider text-xs font-semibold inline-flex items-center justify-center gap-2 w-full"
                >
                  View Profile <ArrowUpRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Co-Founders */}
      <section className="py-20 lg:py-28 bg-[#070707] border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHeading
            eyebrow="Co-Founders"
            title="Built across"
            accent="three countries."
            align="center"
            testid="cofounders-heading"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {coFounders.map((f) => (
              <div
                key={f.id}
                data-testid={`founder-${f.id}`}
                className="bg-[#0a0a0a] border border-white/10 card-hover overflow-hidden"
              >
                <div className="relative aspect-[4/5] image-zoom overflow-hidden">
                  <img src={f.image} alt={f.name} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-black/70 backdrop-blur border border-white/20 text-white">
                    {f.flag} {f.city} · {f.branch}
                  </span>
                </div>
                <div className="p-7 text-center">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#ffb700]">{f.title}</p>
                  <h3 className="font-display text-2xl lg:text-3xl text-white mt-3">{f.name}</h3>
                  <p className="text-sm text-white/65 mt-4 leading-relaxed">{f.short}</p>
                  <Link to={`/instructors/${f.id}`}>
                    <button
                      data-testid={`view-profile-${f.id}`}
                      className="mt-6 border border-white/25 text-white px-6 h-12 uppercase tracking-wider text-xs font-semibold inline-flex items-center justify-center gap-2 w-full hover:bg-[#ff4500] hover:border-[#ff4500] transition-colors"
                    >
                      View Profile <ArrowUpRight className="w-4 h-4" />
                    </button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Teaching Faculty — 4 cards, each clickable to bio page */}
      <section className="py-20 lg:py-28 max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading
          title="Our"
          accent="faculty."
          align="center"
          testid="dance-instructors-heading"
        />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {FACULTY.map((i) => {
            const nonClickable = i.placeholder || i.noProfile;
            return (
            <Link
              key={i.id}
              to={nonClickable ? "#" : `/faculty/${i.id}`}
              onClick={nonClickable ? (e) => e.preventDefault() : undefined}
              data-testid={`faculty-card-${i.id}`}
              className={`group bg-[#0a0a0a] border border-white/10 card-hover overflow-hidden block ${nonClickable ? "cursor-default" : ""} ${i.placeholder ? "opacity-70" : ""}`}
            >
              <div className="relative aspect-square image-zoom overflow-hidden">
                <img
                  src={i.image}
                  alt={i.name}
                  className={`w-full h-full object-cover ${i.imagePosition || "object-top"} transition-all duration-700 ${i.placeholder || i.noPhoto ? "grayscale" : ""}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
                {!nonClickable && (
                  <div className="absolute top-3 right-3 w-9 h-9 bg-[#ff4500]/0 group-hover:bg-[#ff4500] border border-white/30 group-hover:border-[#ff4500] flex items-center justify-center transition-colors">
                    <ArrowUpRight className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              <div className="p-5">
                <h4 className="font-display text-lg lg:text-xl text-white leading-tight">{i.name}</h4>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#ffb700] mt-1.5">
                  {i.placeholder ? "Coming soon" : i.role}
                </p>
                {!i.placeholder && i.years > 0 && (
                  <p className="text-xs text-white/45 mt-2">{i.years}+ years{i.styles && i.styles.length > 0 && i.styles[0] !== "—" ? ` · ${i.styles.slice(0, 2).join(" · ")}` : ""}</p>
                )}
              </div>
            </Link>
            );
          })}
        </div>
      </section>

      {/* Join faculty */}
      <section className="py-20 border-y border-white/10 ember-bg">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-3">◆ Join our faculty</p>
            <h3 className="font-display text-3xl lg:text-4xl text-white leading-tight">
              Are you a choreographer?
            </h3>
            <p className="text-white/65 mt-2 max-w-xl">
              We're always meeting new artists across our three branches. Apply to teach with Phoenix.
            </p>
          </div>
          <Link to="/contact">
            <button
              data-testid="join-faculty-btn"
              className="btn-phoenix bg-[#ff4500] text-white px-8 h-14 uppercase tracking-wider text-sm font-semibold whitespace-nowrap"
            >
              Apply to teach
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
